import { io } from "socket.io-client";
import { config } from "../config/env";
import { authStore } from "../stores/auth.store";

/**
 * SocketService manages the raw socket.io connection.
 * Connection lifecycle (connect / disconnect) is driven by the
 * `useSocketSetup` React hook inside AuthProvider — NOT by module-level
 * side effects. This avoids circular dependencies and timing races.
 */
class SocketService {
  socket = null;

  connect() {
    const token = authStore.token;
    if (!token) return;

    if (this.socket?.connected) {
      // If already connected, ensure we send the join event with current userId
      const userId = authStore.user?.id || authStore.user?._id;
      if (userId) {
        this.socket.emit("join", userId);
        console.debug("Already connected, emitted join room for:", userId);
      }
      return;
    }

    // Extract base host from config.apiBaseUrl (e.g., http://localhost:8000/api -> http://localhost:8000)
    const socketUrl = config.apiBaseUrl.replace(/\/api\/?$/, "");

    this.socket = io(socketUrl, {
      auth: { token },
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.socket.on("connect", () => {
      console.debug("Socket connected successfully with ID:", this.socket.id);
      // Automatically join user's own room on connection
      const userId = authStore.user?.id || authStore.user?._id;
      if (userId) {
        this.socket.emit("join", userId);
        console.debug("Emitted join room for:", userId);
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  emit(event, data) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }
}

const socketService = new SocketService();

export default socketService;
